import { Fragment, type ReactNode } from 'react';

// Tiny renderer for the markdown subset the bot actually emits:
//   **bold**, *italic*, ~~strikethrough~~, `inline code`,
//   [text](url) links and bare https://... auto-links,
//   bulleted lists ("- " / "* "), numbered lists ("1. "),
//   headings (#, ##, ###...), horizontal rules (--- / *** / ___),
//   blockquotes ("> "), fenced code blocks (```...```),
//   blank lines, and GFM tables (| col | col | + |---|---| separator).

// Allowlist URL schemes. javascript:, data:, file: etc. fall through as plain text.
function safeUrl(url: string): string | null {
  const t = url.trim();
  if (/^https?:\/\//i.test(t)) return t;
  if (/^mailto:/i.test(t)) return t;
  if (/^tel:/i.test(t)) return t;
  return null;
}

// Order matters: longer / more-specific patterns first. Markdown link before
// auto-link so [text](url) doesn't get split into prefix + bare-URL match.
const INLINE_RE = /(\*\*[^*\n]+\*\*|~~[^~\n]+~~|`[^`\n]+`|\[[^\]\n]+\]\([^)\s\n]+\)|\*[^*\n]+\*|https?:\/\/[^\s<>"')\]]+)/g;

const LINK_STYLE: React.CSSProperties = {
  color: 'var(--color-accent-pop)',
  textDecoration: 'underline',
  wordBreak: 'break-all',
};

const CODE_STYLE: React.CSSProperties = {
  background: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-default)',
  borderRadius: 4,
  padding: '1px 5px',
  fontSize: '0.9em',
  fontFamily: "'Inter', ui-monospace, monospace",
  direction: 'ltr',
  unicodeBidi: 'embed',
  display: 'inline-block',
};

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const parts = text.split(INLINE_RE);
  parts.forEach((p, i) => {
    if (!p) return;
    const k = `${keyPrefix}-${i}`;

    if (p.startsWith('**') && p.endsWith('**') && p.length > 4) {
      out.push(<strong key={k}>{p.slice(2, -2)}</strong>);
      return;
    }
    if (p.startsWith('~~') && p.endsWith('~~') && p.length > 4) {
      out.push(<s key={k}>{p.slice(2, -2)}</s>);
      return;
    }
    if (p.startsWith('`') && p.endsWith('`') && p.length > 2) {
      out.push(<code key={k} style={CODE_STYLE}>{p.slice(1, -1)}</code>);
      return;
    }
    // Markdown link: [text](url)
    const linkMatch = p.match(/^\[([^\]\n]+)\]\(([^)\s\n]+)\)$/);
    if (linkMatch) {
      const safe = safeUrl(linkMatch[2]);
      if (safe) {
        out.push(
          <a key={k} href={safe} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
            {linkMatch[1]}
          </a>,
        );
      } else {
        out.push(<Fragment key={k}>{p}</Fragment>);
      }
      return;
    }
    if (p.startsWith('*') && p.endsWith('*') && p.length > 2) {
      out.push(<em key={k}>{p.slice(1, -1)}</em>);
      return;
    }
    // Auto-link: bare URL. Strip trailing sentence punctuation that almost
    // certainly isn't part of the URL (the regex doesn't exclude '.' since
    // domains contain it, so do the cleanup here).
    if (/^https?:\/\//i.test(p)) {
      const trailingMatch = p.match(/[.,;:!?]+$/);
      const trail = trailingMatch ? trailingMatch[0] : '';
      const cleanUrl = trail ? p.slice(0, -trail.length) : p;
      const safe = safeUrl(cleanUrl);
      if (safe) {
        out.push(
          <Fragment key={k}>
            <a href={safe} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
              {safe}
            </a>
            {trail}
          </Fragment>,
        );
      } else {
        out.push(<Fragment key={k}>{p}</Fragment>);
      }
      return;
    }
    out.push(<Fragment key={k}>{p}</Fragment>);
  });
  return out;
}

function parseTableCells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function isTableSeparator(line: string): boolean {
  const t = line.trim();
  if (!t.startsWith('|')) return false;
  const cells = parseTableCells(t);
  if (cells.length === 0) return false;
  return cells.every((c) => /^:?-{2,}:?$/.test(c.replace(/\s/g, '')));
}

function TableBlock({
  headers,
  rows,
  keyPrefix,
}: {
  headers: string[];
  rows: string[][];
  keyPrefix: string;
}) {
  return (
    <div style={{ overflowX: 'auto', margin: '6px 0' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 11.5,
          background: 'var(--color-bg-tertiary)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <thead>
          <tr style={{ background: 'rgba(210,233,254,0.06)' }}>
            {headers.map((h, i) => (
              <th
                key={`${keyPrefix}-h-${i}`}
                style={{
                  padding: '6px 9px',
                  textAlign: 'start',
                  fontWeight: 700,
                  fontSize: 11,
                  color: 'var(--color-text-secondary)',
                  borderBottom: '1px solid var(--color-border-default)',
                  whiteSpace: 'nowrap',
                }}
              >
                {renderInline(h, `${keyPrefix}-h-${i}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rIdx) => (
            <tr key={`${keyPrefix}-r-${rIdx}`}>
              {row.map((cell, cIdx) => (
                <td
                  key={`${keyPrefix}-r-${rIdx}-c-${cIdx}`}
                  style={{
                    padding: '6px 9px',
                    borderTop: rIdx > 0 ? '1px solid var(--color-border-default)' : 'none',
                    verticalAlign: 'top',
                    color: 'var(--color-text-primary)',
                    lineHeight: 1.5,
                    wordBreak: 'break-word',
                  }}
                >
                  {renderInline(cell, `${keyPrefix}-r-${rIdx}-c-${cIdx}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeBlock({ code, keyPrefix }: { code: string; keyPrefix: string }) {
  return (
    <pre
      key={keyPrefix}
      style={{
        background: 'var(--color-bg-tertiary)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 8,
        padding: '8px 10px',
        margin: '6px 0',
        fontSize: 11.5,
        lineHeight: 1.5,
        fontFamily: "'Inter', ui-monospace, monospace",
        direction: 'ltr',
        unicodeBidi: 'embed',
        overflowX: 'auto',
        whiteSpace: 'pre',
        color: 'var(--color-text-primary)',
      }}
    >
      <code>{code}</code>
    </pre>
  );
}

function Blockquote({ lines, keyPrefix }: { lines: string[]; keyPrefix: string }) {
  return (
    <blockquote
      style={{
        margin: '6px 0',
        padding: '4px 10px',
        borderInlineStart: '3px solid var(--color-accent-pop)',
        background: 'rgba(210,233,254,0.05)',
        color: 'var(--color-text-secondary)',
      }}
    >
      {lines.map((ln, idx) => (
        <div key={`${keyPrefix}-${idx}`} style={{ whiteSpace: 'pre-wrap' }}>
          {renderInline(ln, `${keyPrefix}-${idx}`)}
        </div>
      ))}
    </blockquote>
  );
}

type ListState = { ordered: boolean; items: string[] };

export function renderMarkdown(text: string): ReactNode {
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let list: ListState | null = null;

  const flushList = () => {
    if (!list || list.items.length === 0) {
      list = null;
      return;
    }
    const { ordered, items } = list;
    list = null;
    const listStyle = {
      margin: '4px 0',
      paddingInlineStart: 22,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 2,
    };
    if (ordered) {
      blocks.push(
        <ol key={`ol-${blocks.length}`} style={listStyle}>
          {items.map((it, j) => (
            <li key={j}>{renderInline(it, `li-${blocks.length}-${j}`)}</li>
          ))}
        </ol>,
      );
    } else {
      blocks.push(
        <ul key={`ul-${blocks.length}`} style={listStyle}>
          {items.map((it, j) => (
            <li key={j}>{renderInline(it, `li-${blocks.length}-${j}`)}</li>
          ))}
        </ul>,
      );
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Fenced code block: ```lang ... ```. Capture everything verbatim until
    // the closing fence (or EOF, in case the bot left it dangling).
    if (trimmed.startsWith('```')) {
      flushList();
      const codeLines: string[] = [];
      let j = i + 1;
      while (j < lines.length && !lines[j].trim().startsWith('```')) {
        codeLines.push(lines[j]);
        j++;
      }
      blocks.push(
        <CodeBlock key={`code-${i}`} code={codeLines.join('\n')} keyPrefix={`code-${i}`} />,
      );
      i = j; // skip closing fence (if present)
      continue;
    }

    // Blockquote: one or more consecutive "> " lines.
    if (/^>\s?/.test(line)) {
      flushList();
      const quoteLines: string[] = [line.replace(/^>\s?/, '')];
      let j = i + 1;
      while (j < lines.length && /^>\s?/.test(lines[j])) {
        quoteLines.push(lines[j].replace(/^>\s?/, ''));
        j++;
      }
      blocks.push(
        <Blockquote key={`bq-${i}`} lines={quoteLines} keyPrefix={`bq-${i}`} />,
      );
      i = j - 1;
      continue;
    }

    // GFM table: "| ... |" header line followed by a "|---|---|" separator.
    if (
      trimmed.startsWith('|') &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      flushList();
      const headers = parseTableCells(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (
        j < lines.length &&
        lines[j].trim().startsWith('|') &&
        !isTableSeparator(lines[j])
      ) {
        rows.push(parseTableCells(lines[j]));
        j++;
      }
      blocks.push(
        <TableBlock
          key={`tbl-${i}`}
          headers={headers}
          rows={rows}
          keyPrefix={`tbl-${i}`}
        />,
      );
      i = j - 1;
      continue;
    }

    // Heading: 1-6 hashes followed by space and content.
    const headingMatch = line.match(/^(#{1,6})\s+(.+?)\s*#*$/);
    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      const content = headingMatch[2];
      const fontSize = level === 1 ? 15 : level === 2 ? 13.5 : 12.5;
      blocks.push(
        <div
          key={`h-${i}`}
          style={{
            margin: '6px 0 2px',
            fontSize,
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            lineHeight: 1.35,
          }}
        >
          {renderInline(content, `h-${i}`)}
        </div>,
      );
      continue;
    }

    // Horizontal rule: a line that is only --- / *** / ___ (3 or more).
    if (/^-{3,}$|^\*{3,}$|^_{3,}$/.test(trimmed)) {
      flushList();
      blocks.push(
        <hr
          key={`hr-${i}`}
          style={{
            border: 'none',
            borderTop: '1px solid var(--color-border-default)',
            margin: '8px 0',
          }}
        />,
      );
      continue;
    }

    // Lists: bulleted or numbered.
    const bulletMatch = line.match(/^\s*[-*]\s+(.+)$/);
    const numberedMatch = bulletMatch ? null : line.match(/^\s*\d+[.)]\s+(.+)$/);
    if (bulletMatch || numberedMatch) {
      const ordered = !!numberedMatch;
      const itemText = bulletMatch ? bulletMatch[1] : numberedMatch![1];
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { ordered, items: [] };
      list.items.push(itemText);
      continue;
    }

    flushList();
    if (trimmed) {
      blocks.push(
        <div key={`p-${i}`} style={{ whiteSpace: 'pre-wrap' }}>
          {renderInline(line, `p-${i}`)}
        </div>,
      );
    } else {
      blocks.push(<div key={`sp-${i}`} style={{ height: 4 }} />);
    }
  }
  flushList();

  return <>{blocks}</>;
}
